/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
    NotificationActions,
    NotificationContainer,
    STATES,
    TERRITORIES,
    ARMED_FORCES,
} from 'sarsaparilla';
import { useDocumentTitle } from 'shared-ui/utils/seo-utils';

import FindingTree from '../components/splashPage/FindingTree';
import TreeMap from '../components/splashPage/TreeMap';
import HowToCut from '../components/splashPage/HowToCut';
// Not including the Ornament this year, so commenting out for now.
//import ChristmasOrnamentPromo from '../components/splashPage/ChristmasOrnamentPromo';
import HeroWithAutoComplete from '../components/splashPage/HeroWithAutoComplete';

import { fetchLocations } from '../actions/locations';
import { locationsSelector as locationsSel } from '../reducers';
import { onError } from '../utils';
import type { RootState, AppDispatch } from '../../dev/store';
import type { TreePermitData } from '../../types/permit';

interface TreePermitProps {
    getTreePermits: () => Promise<Record<string, unknown>>;
    locations: Array<TreePermitData>;
    notify: (type: string, message: string) => void;
    setError: (error: { message?: string }, defaultErr: string) => string;
}

function TreePermit({ getTreePermits, locations, notify, setError }: TreePermitProps) {
    const states: Record<string, string> = useMemo(
        () =>
            STATES.reduce(
                (acc: Record<string, string>, item) => ({
                    ...acc,
                    [item.value]: item.label,
                }),
                {}
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [STATES]
    );
    const territories: Record<string, string> = useMemo(
        () =>
            TERRITORIES.reduce(
                (acc: Record<string, string>, item) => ({
                    ...acc,
                    [item.value]: item.label,
                }),
                {}
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [TERRITORIES]
    );
    const armedForces: Record<string, string> = useMemo(
        () =>
            ARMED_FORCES.reduce(
                (acc: Record<string, string>, item) => ({
                    ...acc,
                    [item.value]: item.label,
                }),
                {}
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [ARMED_FORCES]
    );

    const isState = (facility: TreePermitData) => {
        return !!STATES.find((item) => {
            return facility.state === item.value;
        });
    };

    const isTerritory = (facility: TreePermitData) => {
        return !!TERRITORIES.find((item) => {
            return facility.state === item.value;
        });
    };

    const isArmedForce = (facility: TreePermitData) => {
        return !!ARMED_FORCES.find((item) => {
            return facility.state === item.value;
        });
    };

    const facilitiesWithState = locations
        ?.map((item) => {
            let facilityWithState: TreePermitData | undefined;
            switch (true) {
                case isState(item):
                    facilityWithState = { ...item, state: states[item.state] };
                    break;
                case isTerritory(item):
                    facilityWithState = { ...item, state: territories[item.state] };
                    break;
                case isArmedForce(item):
                    facilityWithState = { ...item, state: armedForces[item.state] };
                    break;
                default:
            }

            return facilityWithState;
        })
        .filter((item): item is TreePermitData => !!item)
        .sort((a, b) => a.name.localeCompare(b.name))
        .sort((a, b) => a.state.localeCompare(b.state));

    const notifyError = (e: { message?: string }) => {
        const err = setError(e, 'There was a problem loading the tree permits.');
        notify('error', err);
    };

    useEffect(() => {
        getTreePermits().catch((e: { message?: string }) => notifyError(e));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useDocumentTitle(`Christmas Tree Permits - ${process.env.SITE_NAME}`);

    return (
        <div className="tree-permit-splash-wrap" role="main">
            <HeroWithAutoComplete locations={facilitiesWithState} />
            <NotificationContainer className={'mb-0 mx-6 mt-4'} />
            <FindingTree />
            {/*<ChristmasOrnamentPromo />*/}
            <TreeMap locations={facilitiesWithState} />
            <HowToCut />
        </div>
    );
}

const mapStateToProps = (state: RootState) => ({
    locations: locationsSel.getLocations(state) as TreePermitData[],
});

const mapDispatchToProps = (dispatch: AppDispatch) =>
    bindActionCreators(
        {
            getTreePermits: fetchLocations,
            notify: NotificationActions.setNotification,
            setError: onError,
        },
        dispatch
    );

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default connect(mapStateToProps, mapDispatchToProps)(TreePermit as any);
